import { AlertColor } from "@mui/material";
import { AxiosError } from "axios";
import { create } from "zustand";
import { createApi } from "../../../utils/createApi";
import { abortableWait } from "../../../utils/abortableWait";

/**
 * Axis tab on sites whose cameras sit behind a WireGuard tunnel (ENG-2822).
 *
 * Admin's servers can't reach those cameras, so actuate_admin:
 * - answers the tab's reads and device actions (recipient, action_rule,
 *   application, trigger_motion, deletes) with 409 and the usual
 *   MessageSerializer list (`[{message, level_tag, level}]`);
 * - answers POST /axis/{camera}/action_rule/ with 202 and a command-status
 *   body, after launching a connector-tools Job on the site's network that
 *   installs the rule for this camera's device (one rule per view);
 * - reports the Job's outcome on the same action_status endpoint the site
 *   actions poll (`?action=fix_axis_multisensor_rules`).
 *
 * The state lives in a store keyed by camera id rather than on the AxisTab
 * element, because the tab is rebuilt from EditCamera's state on every tab
 * switch: a message or a running job must survive the operator clicking away
 * and back.
 */

export const AXIS_RULE_JOB_ACTION = "fix_axis_multisensor_rules";
export const AXIS_RULE_JOB_POLL_INTERVAL_MS = 3000;
export const AXIS_RULE_JOB_POLL_ATTEMPTS = 40; // ~2 minutes

export type AxisRuleJobCommandStatus =
  | "pending"
  | "success"
  | "partial"
  | "failed"
  | "timed_out";

export interface AxisRuleJobCamera {
  camera_id: number | string | null;
  camera_name?: string | null;
  device?: string | null;
  sensor?: number | string | null;
  status: string;
  detail?: string | null;
  [key: string]: unknown;
}

export interface AxisRuleJobResult {
  apply?: boolean;
  cameras?: AxisRuleJobCamera[];
  error?: string | null;
}

// Both the 202 body and GET customer/{id}/action_status/?action=...
export interface AxisRuleJobStatusResponse {
  id: number | null;
  action: string;
  status: AxisRuleJobCommandStatus | null;
  result: AxisRuleJobResult | null;
  request_date: string;
  completed_at: string | null;
}

export type AxisRuleJobPhase = "pending" | "done";

export interface AxisRuleJobState {
  phase: AxisRuleJobPhase;
  severity: AlertColor;
  message: string;
  details: string[];
}

export const AXIS_RULE_JOB_PENDING_MESSAGE =
  "Adding the action rule from the site's network. This usually takes under a minute.";
export const AXIS_RULE_JOB_TIMEOUT_MESSAGE =
  "The action rule job hasn't reported back yet. It may still be running — check again in a few minutes, or try again.";
export const AXIS_DEVICE_UNREACHABLE_FALLBACK =
  "This camera can't be reached from Actuate's servers, so its Axis settings can't be shown here.";

const STATUS_LABELS: Record<string, string> = {
  FIXED: "fixed",
  ALREADY_OK: "already OK",
  FAILED: "failed",
  STALE_LEFT: "old rule left in place",
  UNREACHABLE: "unreachable",
  HOST_REFUSED: "refused by the camera",
  NO_MOTION: "no motion detection",
  NO_SENSOR: "no sensor",
  BAD_SENSOR: "bad sensor",
  DEVICE_BLOCKED: "device blocked",
  SINGLE_SENSOR: "single sensor",
};

export const axisRuleStatusLabel = (status: string): string =>
  STATUS_LABELS[String(status).toUpperCase()] ??
  String(status).toLowerCase().replace(/_/g, " ");

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * The server's message when a 409 says this camera can't be reached from
 * admin's servers; null for any other error, which keeps its existing path.
 */
export const axisDeviceUnreachableMessage = (
  reason: AxiosError | undefined | null
): string | null => {
  if (reason?.response?.status !== 409) return null;
  const data = reason.response.data as
    | Array<{ message?: unknown }>
    | { message?: unknown; detail?: unknown }
    | undefined;
  if (Array.isArray(data)) {
    const message = data.find((m) => m && typeof m.message === "string");
    if (message) return String(message.message);
  } else if (data && typeof data.message === "string") {
    return data.message;
  } else if (data && typeof data.detail === "string") {
    return data.detail;
  }
  return AXIS_DEVICE_UNREACHABLE_FALLBACK;
};

/**
 * Turns a finished action_status row (or a poll timeout) into what the Axis
 * tab shows: this camera's row, then a count of the device's views by status.
 */
export const summarizeAxisRuleJob = (
  outcome: AxisRuleJobStatusResponse | "timeout",
  cameraId: string | number
): AxisRuleJobState => {
  if (outcome === "timeout" || outcome.status === "timed_out") {
    return {
      phase: "done",
      severity: "info",
      message: AXIS_RULE_JOB_TIMEOUT_MESSAGE,
      details: [],
    };
  }

  const cameras = outcome.result?.cameras ?? [];
  const row = cameras.find((c) => String(c.camera_id) === String(cameraId));
  const details: string[] = [];

  if (row) {
    details.push(
      `This camera: ${axisRuleStatusLabel(row.status)}${
        row.detail ? ` — ${row.detail}` : ""
      }.`
    );
    const views = row.device
      ? cameras.filter((c) => c.device === row.device)
      : [row];
    if (views.length > 1) {
      const counts: string[] = [];
      const seen: Record<string, number> = {};
      views.forEach((c) => {
        const label = axisRuleStatusLabel(c.status);
        if (seen[label] === undefined) {
          seen[label] = counts.length;
          counts.push(label);
        }
      });
      const tally = counts.map(
        (label) =>
          `${
            views.filter((c) => axisRuleStatusLabel(c.status) === label).length
          } ${label}`
      );
      details.push(`${plural(views.length, "view")}: ${tally.join(", ")}.`);
    }
  }

  switch (outcome.status) {
    case "success":
      return {
        phase: "done",
        severity: "success",
        message: "Action rule job finished.",
        details,
      };
    case "partial":
      return {
        phase: "done",
        severity: "warning",
        message: "Action rule job finished, but not every view was fixed.",
        details,
      };
    default: {
      const reason =
        outcome.result?.error ||
        row?.detail ||
        (row ? capitalize(axisRuleStatusLabel(row.status)) : "") ||
        "unknown error";
      return {
        phase: "done",
        severity: "error",
        message: `Action rule job failed: ${reason}`,
        details: outcome.result?.error ? details : details.slice(1),
      };
    }
  }
};

interface AxisDeviceJobStore {
  unreachable: Record<string, string>;
  ruleJobs: Record<string, AxisRuleJobState>;
  setUnreachable: (cameraId: string | number, message: string | null) => void;
  setRuleJob: (
    cameraId: string | number,
    state: AxisRuleJobState | null
  ) => void;
}

export const useAxisDeviceJobStore = create<AxisDeviceJobStore>((set) => ({
  unreachable: {},
  ruleJobs: {},
  setUnreachable: (cameraId, message) =>
    set((state) => {
      const unreachable = { ...state.unreachable };
      if (message) unreachable[String(cameraId)] = message;
      else delete unreachable[String(cameraId)];
      return { unreachable };
    }),
  setRuleJob: (cameraId, job) =>
    set((state) => {
      const ruleJobs = { ...state.ruleJobs };
      if (job) ruleJobs[String(cameraId)] = job;
      else delete ruleJobs[String(cameraId)];
      return { ruleJobs };
    }),
}));

export interface AxisRuleJobPollOptions {
  attempts?: number;
  intervalMs?: number;
  signal?: AbortSignal;
}

/**
 * Polls action_status until the Command the 202 announced leaves `pending`.
 * With an id, admin is asked for exactly that row (`&id=`): each Add Action
 * Rule click has its own row, and the site's newest may be another camera's.
 * Without one (dispatch faked outside prod), matched by a request_date no
 * older than the dispatch's (both stamped server-side, so no clock skew). A
 * 404 (row not visible yet) or a transient error is retried like a pending
 * row. Returns the finished row, "timeout", or null when the signal aborts.
 */
export const pollAxisRuleJob = async (
  siteId: string | number,
  dispatch: AxisRuleJobStatusResponse,
  options: AxisRuleJobPollOptions = {}
): Promise<AxisRuleJobStatusResponse | "timeout" | null> => {
  const attempts = options.attempts ?? AXIS_RULE_JOB_POLL_ATTEMPTS;
  const intervalMs = options.intervalMs ?? AXIS_RULE_JOB_POLL_INTERVAL_MS;
  const signal = options.signal;
  const client = createApi(``);
  const dispatchedAt = Date.parse(dispatch.request_date);
  const hasId = dispatch.id !== null && dispatch.id !== undefined;
  const statusUrl =
    `customer/${siteId}/action_status/?action=${AXIS_RULE_JOB_ACTION}` +
    (hasId ? `&id=${dispatch.id}` : "");

  const isThisJob = (data: AxisRuleJobStatusResponse) => {
    if (hasId) {
      return data.id === dispatch.id;
    }
    if (Number.isNaN(dispatchedAt)) return true;
    return Date.parse(data.request_date) >= dispatchedAt;
  };

  for (let attempt = 0; attempt < attempts; attempt++) {
    await abortableWait(intervalMs, signal);
    if (signal?.aborted) return null;
    try {
      const response = await client.get<AxisRuleJobStatusResponse>(statusUrl, {
        signal,
      });
      if (signal?.aborted) return null;
      const data = response.data;
      if (data && isThisJob(data) && data.status && data.status !== "pending") {
        return data;
      }
    } catch {
      // 404 (no row yet), transient error, or aborted: the loop decides.
    }
  }
  return signal?.aborted ? null : "timeout";
};
